import React, { useEffect, useRef, useState } from "react";
import {
  FaEnvelope,
  FaExternalLinkAlt,
  FaFacebookF,
  FaInstagram,
  FaLinkedinIn,
  FaPhoneAlt,
  FaTimes,
  FaTrophy,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";

import { getSettings } from "../../services/settingsServices";

const APP_LOGO = "/logo_monapplitournoi_512.png";

const FOOTER_LINKS = [
  { label: "Accueil", id: "accueil" },
  { label: "Présentation", id: "description" },
  { label: "Diaporama", id: "diaporama" },
  { label: "Tarifs", id: "tarifs" },
  { label: "Contact", id: "contact" },
];

const LEGAL_LINKS = [
  { label: "Mentions légales", href: "/mentions-legales" },
  { label: "Politique de confidentialité", href: "/politique-confidentialite" },
  { label: "Conditions d’utilisation", href: "/conditions-utilisation" },
  { label: "Page d'aide", href: "/aide" },
];

const BusinessCard = ({ open, onClose }) => {
  const dialogRef = useRef(null);
  const lastFocusRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const originalOverflow = document.body.style.overflow;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose?.();
    };

    lastFocusRef.current = document.activeElement;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    setTimeout(() => {
      dialogRef.current?.focus();
    }, 0);

    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener("keydown", handleKeyDown);
      lastFocusRef.current?.focus?.();
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-950/70 px-4 backdrop-blur-sm"
      onMouseDown={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Carte de visite"
        ref={dialogRef}
        tabIndex={-1}
        onMouseDown={(event) => event.stopPropagation()}
        className="relative w-full max-w-2xl overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-2xl outline-none dark:border-white/10 dark:bg-slate-900"
      >
        <div className="pointer-events-none absolute -left-20 -top-20 h-64 w-64 rounded-full bg-orange-400/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-20 -right-20 h-64 w-64 rounded-full bg-blue-400/20 blur-3xl" />

        <button
          type="button"
          onClick={onClose}
          aria-label="Fermer"
          className="absolute right-4 top-4 z-20 inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 shadow-sm transition hover:bg-slate-100 hover:text-orange-600 dark:border-white/10 dark:bg-white/10 dark:text-white dark:hover:bg-white/20"
        >
          <FaTimes className="h-4 w-4" />
        </button>

        <div className="relative grid gap-6 p-5 sm:p-7 md:grid-cols-[150px_1fr] md:items-center">
          <div className="flex justify-center md:justify-start">
            <img
              src="https://res.cloudinary.com/dj0c0zuje/image/upload/v1744184384/PXL_20241115_211013575.MP_1_h7lbn9.jpg"
              alt="Laurent Victorin"
              loading="lazy"
              className="h-32 w-32 rounded-[2rem] object-cover shadow-xl ring-4 ring-orange-100 dark:ring-white/10"
            />
          </div>

          <div className="text-center md:text-left">
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-orange-600 dark:text-orange-300">
              Créateur MonAppliClub
            </p>

            <h3 className="mt-2 text-2xl font-bold text-slate-950 dark:text-white">
              Laurent VICTORIN
            </h3>

            <p className="mt-2 text-sm leading-relaxed text-slate-600 dark:text-slate-300">
              Auto-Entrepreneur • Développeur Web & Mobile • Président et
              enseignant de Judo
            </p>

            <p className="mt-4 text-sm leading-relaxed text-slate-700 dark:text-slate-300">
              Passionné de développement et de judo, je conçois des applications
              et sites web pour simplifier la gestion des clubs sportifs et des
              compétitions.
            </p>

            <div className="mt-4 flex flex-col gap-2 text-sm sm:flex-row sm:flex-wrap sm:items-center">
              <a
                href="mailto:monappliclub@gmail.com"
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-2 font-bold text-slate-700 transition hover:border-orange-200 hover:text-orange-600 dark:border-white/10 dark:bg-white/[0.04] dark:text-white dark:hover:border-orange-400/40 dark:hover:text-orange-300"
              >
                <FaEnvelope className="h-4 w-4" />
                monappliclub@gmail.com
              </a>

              <a
                href="tel:+33680727707"
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-2 font-bold text-slate-700 transition hover:border-orange-200 hover:text-orange-600 dark:border-white/10 dark:bg-white/[0.04] dark:text-white dark:hover:border-orange-400/40 dark:hover:text-orange-300"
              >
                <FaPhoneAlt className="h-4 w-4" />
                06 80 72 77 07
              </a>
            </div>

            <div className="mt-5 flex flex-col gap-3 sm:flex-row">
              <a
                href="https://www.monappliclub.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-2xl bg-orange-500 px-4 py-2 text-sm font-bold text-white shadow-lg shadow-orange-500/20 transition hover:-translate-y-0.5 hover:bg-orange-600"
              >
                MonAppliClub
                <FaExternalLinkAlt className="h-3.5 w-3.5" />
              </a>

              <a
                href="https://profile.vkard.io/8f8e4d95-cf28-40b0-afc6-2ec677889d49/ab099714aa"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex min-h-11 items-center justify-center rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-800 shadow-sm transition hover:-translate-y-0.5 hover:border-orange-200 hover:text-orange-600 dark:border-white/10 dark:bg-white/[0.04] dark:text-white dark:hover:border-orange-400/40 dark:hover:text-orange-300"
              >
                Ma vCard
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Footer = () => {
  const navigate = useNavigate();

  const [settings, setSettings] = useState({
    nom_club: "",
    facebook_club: "",
    instagram_club: "",
  });

  const [openCard, setOpenCard] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const fetchSettings = async () => {
      try {
        const data = await getSettings();

        if (!cancelled && Array.isArray(data) && data.length > 0) {
          setSettings((current) => ({
            ...current,
            ...data[0],
          }));
        }
      } catch (error) {
        console.error("Erreur getSettings Footer :", error);
      }
    };

    fetchSettings();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleNavigate = (id) => {
    const element = document.getElementById(id);

    if (element) {
      const y = element.getBoundingClientRect().top + window.pageYOffset - 120;

      window.scrollTo({
        top: y,
        behavior: "smooth",
      });

      return;
    }

    navigate("/", {
      state: {
        scrollTo: id,
      },
    });
  };

  return (
    <>
      <footer className="relative overflow-hidden bg-slate-950 text-white">
        <div className="pointer-events-none absolute -left-24 top-0 h-80 w-80 rounded-full bg-blue-500/15 blur-3xl" />
        <div className="pointer-events-none absolute -right-24 bottom-0 h-80 w-80 rounded-full bg-orange-500/15 blur-3xl" />

        <div className="relative mx-auto max-w-[1380px] px-4 py-12 sm:px-6 lg:px-8">
          <div className="grid gap-8 lg:grid-cols-[1.2fr_0.8fr_0.8fr]">
            <div>
              <div className="flex items-center gap-4">
                <div className="relative shrink-0">
                  <div className="absolute inset-0 rounded-3xl bg-orange-400/20 blur-xl" />

                  <img
                    src={APP_LOGO}
                    alt="Logo MonAppliTournoi"
                    className="relative h-20 w-20 rounded-3xl object-contain ring-1 ring-white/10 sm:h-24 sm:w-24"
                    loading="lazy"
                  />
                </div>

                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.24em] text-orange-300">
                    MonAppliTournoi
                  </p>

                  <h2 className="mt-1 text-2xl font-bold sm:text-3xl">
                    Gestion de tournois et compétitions
                  </h2>
                </div>
              </div>

              <p className="mt-5 max-w-xl text-sm leading-relaxed text-slate-300 sm:text-base">
                Une solution web moderne pour préparer vos compétitions,
                organiser les pesées, générer les poules ou tableaux, suivre les
                combats et publier les résultats en temps réel.
              </p>

              <div className="mt-5 flex flex-wrap gap-3">
                <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-2 text-xs font-bold text-slate-200">
                  <FaTrophy className="text-orange-300" />
                  Tournois interclubs
                </span>

                <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-2 text-xs font-bold text-slate-200">
                  Résultats publics & QR Code
                </span>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-bold uppercase tracking-[0.22em] text-slate-400">
                Navigation
              </h3>

              <nav className="mt-4 grid gap-2">
                {FOOTER_LINKS.map((link) => (
                  <button
                    key={link.id}
                    type="button"
                    onClick={() => handleNavigate(link.id)}
                    className="group inline-flex items-center justify-between rounded-2xl px-3 py-2 text-left text-sm font-bold text-slate-300 transition hover:bg-white/10 hover:text-orange-300"
                  >
                    {link.label}
                    <span className="opacity-0 transition group-hover:translate-x-1 group-hover:opacity-100">
                      →
                    </span>
                  </button>
                ))}
              </nav>
            </div>

            <div>
              <h3 className="text-sm font-bold uppercase tracking-[0.22em] text-slate-400">
                Informations
              </h3>

              <nav className="mt-4 grid gap-2">
                {LEGAL_LINKS.map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    className="group inline-flex items-center justify-between rounded-2xl px-3 py-2 text-sm font-bold text-slate-300 transition hover:bg-white/10 hover:text-orange-300"
                  >
                    {link.label}
                    <span className="opacity-0 transition group-hover:translate-x-1 group-hover:opacity-100">
                      →
                    </span>
                  </a>
                ))}
              </nav>

              <div className="mt-5 flex items-center gap-3">
                {settings.facebook_club && (
                  <a
                    href={settings.facebook_club}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-white/10 bg-white/10 text-white transition hover:-translate-y-0.5 hover:bg-orange-500"
                    aria-label="Facebook"
                    title="Facebook"
                  >
                    <FaFacebookF />
                  </a>
                )}

                {settings.instagram_club && (
                  <a
                    href={settings.instagram_club}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-white/10 bg-white/10 text-white transition hover:-translate-y-0.5 hover:bg-orange-500"
                    aria-label="Instagram"
                    title="Instagram"
                  >
                    <FaInstagram />
                  </a>
                )}

                <a
                  href="https://www.linkedin.com/in/laurent-victorin/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-white/10 bg-white/10 text-white transition hover:-translate-y-0.5 hover:bg-orange-500"
                  aria-label="LinkedIn"
                  title="LinkedIn"
                >
                  <FaLinkedinIn />
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="relative border-t border-white/10">
          <div className="mx-auto flex max-w-[1380px] flex-col items-center justify-between gap-3 px-4 py-4 text-center text-sm text-slate-400 sm:px-6 lg:flex-row lg:px-8 lg:text-left">
            <p>
              © {new Date().getFullYear()} — Tous droits réservés —
              MonAppliTournoi
            </p>

            <p>
              Créé par{" "}
              <button
                type="button"
                onClick={() => setOpenCard(true)}
                className="font-bold text-orange-300 underline underline-offset-4 transition hover:text-orange-200"
              >
                Laurent VICTORIN
              </button>{" "}
              ·{" "}
              <a
                href="https://www.monappliclub.com"
                target="_blank"
                rel="noopener noreferrer"
                className="font-bold text-slate-200 underline-offset-4 transition hover:text-orange-300 hover:underline"
              >
                MonAppliClub
              </a>
            </p>
          </div>
        </div>
      </footer>

      <BusinessCard open={openCard} onClose={() => setOpenCard(false)} />
    </>
  );
};

export default Footer;